import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { StorageService, StorageKey } from '../services/storage.service';
import { JwtUtils } from '../utils/jwt.utils';

/** Basic auth guard — checks if user is logged in */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated) {
    return true;
  }
  return router.createUrlTree(['/login']);
};

/** Platform admin guard — only PlatformAdmin can access */
export const platformGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated) {
    return router.createUrlTree(['/login']);
  }

  if (authService.isPlatformAdmin) {
    return true;
  }

  // Tenant users trying to access platform → redirect to tenant area
  if (authService.isTenantAdmin) {
    return router.createUrlTree(['/tenant']);
  }

  return router.createUrlTree(['/login']);
};

/** Tenant admin guard — Admin, Manager, BackOffice can access the tenant shell */
export const tenantAdminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated) {
    return router.createUrlTree(['/login']);
  }

  if (authService.isTenantAdmin) {
    return true;
  }

  // Cashier/Waiter/Chef cannot access tenant admin area
  return router.createUrlTree(['/login']);
};

/** POS guard — only POS-capable roles can access */
export const posGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const storage = inject(StorageService);

  // Preserve the attempted URL so the PIN screen can return here after login.
  // Used by the edge client's deep-linked enrolment wizard
  // (/pos/settings/biometric-enrol), which bounces here when logged out.
  const pinTree = (code: string) =>
    router.createUrlTree(['/pos/s', code], { queryParams: { returnUrl: state.url } });

  // Check standalone POS mode first (direct POS link login)
  const standaloneCode = storage.getRaw(StorageKey.POS_STANDALONE_CODE);
  const posToken = storage.getRaw(StorageKey.POS_TOKEN);

  // Standalone mode: user logged in via direct POS link (/pos/s/:code)
  if (standaloneCode && posToken) {
    // Hub-only offline (post-IDB-removal): expired token = kick to PIN
    // unconditionally. There is no per-device PIN cache to tolerate a
    // stale token against. When the hub is reachable, the PIN screen's
    // /auth/pin-login call routes to the hub (HM-19) and re-issues a
    // fresh POS token.
    if (JwtUtils.isExpired(posToken)) {
      console.warn('[posGuard] POS token expired, redirecting to PIN screen');
      storage.remove(StorageKey.POS_AUTH);
      storage.remove(StorageKey.POS_TOKEN);
      return pinTree(standaloneCode);
    }
    return true;
  }

  // Admin-based access: user logged in via admin panel.
  // EXPIRY-LOGIN — hasValidSession, not isAuthenticated: the latter logs out
  // an expired admin token, and that logout's navigate('/login') overrides the
  // PIN redirect below. Edge terminals keep the setup admin token long after
  // it expires, so a store-code terminal with no POS token went to /login.
  if (authService.hasValidSession) {
    if (authService.hasPosAccess()) {
      return true;
    }
    // Authenticated but no POS permission
    return router.createUrlTree(['/tenant']);
  }

  // Not authenticated at all. On a dedicated / direct-link POS device we
  // must return to the store PIN screen, never the admin login — the edge
  // client shell only ever drives the POS surface. (standaloneCode was
  // resolved at the top of the guard.)
  if (standaloneCode) {
    return pinTree(standaloneCode);
  }
  return router.createUrlTree(['/login']);
};

/** KDS guard — only KDS-capable roles can access */
export const kdsGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated) {
    return router.createUrlTree(['/login']);
  }

  if (authService.hasKdsAccess()) {
    return true;
  }

  return router.createUrlTree(['/tenant']);
};
